import {
    useCallback,
    useMemo,
} from 'react'

import useDelayedLoadOptions from 'app/hooks/useDelayedLoadOptions'
import SkyNetLink, {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import Typeahead from 'app/shared-components/Typeahead'
import request from 'app/requests/FreightForwarder.request'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes/skyNetRoutes.utils'
import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'

import useGetFreightForwarder from './useGetFreightForwarder'

type Props = {
    name?: string,
    value?: {
        id: number | string,
    },
    onChange: (value: { id: number }) => void,
    title?: string,
    disabled?: boolean,
    className?: string,
    required?: boolean,
    noneOption?: boolean,
    freightRateServiceProviderCode?: string,
}

const FreightForwarderSelector = ({
    name = '',
    value,
    onChange,
    title = 'Freight Forwarder',
    className = '',
    disabled = false,
    required = false,
    noneOption = true,
    freightRateServiceProviderCode = undefined,
}: Partial<Props>) => {
    // TODO-JM: how to test freightRateServiceProviderCode in request -???
    const loadOptions = useDelayedLoadOptions({
        ...request.filterRequest({}),
        includeFilters: {
            freightRateServiceProviderCode: freightRateServiceProviderCode
                ? [freightRateServiceProviderCode] : undefined,
        },
        convertion: (item) => {
            return item.map(({
                id,
                freightForwarderName,
            }) => {
                return {
                    value: id,
                    label: freightForwarderName,
                    id,
                }
            })
        },
    })

    const {
        data,
        isDataReady,
    } = useGetFreightForwarder(value?.id)

    const {
        FreightForwarders: FreightForwardersRoutes,
    } = useOrderManagementRoutes()

    const onChangeValue = useCallback((newValue) => {
        const convertedValue = newValue ? {
            id: newValue,
        } : undefined

        onChange(convertedValue)
    }, [onChange])

    const labelData = useMemo(() => {
        if (data) {
            return {
                label: `${data?.freightForwarderName}`,
            }
        }

        return null
    }, [data])

    const link = useMemo(() => {
        return value?.id ? generateSkyNetLink({
            domainPath: FreightForwardersRoutes.Overview.path,
            params: {
                key: value?.id,
            },
        }) : null
    }, [
        FreightForwardersRoutes.Overview.path,
        value?.id,
    ])

    if (isDataReady && disabled) {
        return link ? (
            <SkyNetLink
                type={TypeLink.EXTERNAL}
                size={SizeLink.S}
                link={link}
                text={labelData?.label}
                title={title}
            />
        ) : null
    }

    return (
        <Typeahead
            name={name}
            title={title}
            className={className}
            onChange={onChangeValue}
            loadOptions={loadOptions}
            labelData={labelData}
            labelField="label"
            value={value?.id}
            disabled={disabled}
            isRequired={required}
            noneOption={noneOption}
        />
    )
}

export default FreightForwarderSelector
